import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client/react';
import {
  Star, Clock, Users, MapPin, Calendar, ArrowRight,
  ShieldCheck, Share2, Heart, Languages,
  Utensils, Navigation, Phone, Mail,
  ChevronLeft, Zap, Camera,
  Sunrise, Award, TrendingUp, CheckCircle2, XCircle, Quote,
  Leaf, Compass, AlertCircle, Mountain, Waves, Building2,
} from 'lucide-react';
import { GET_TOUR_DETAILS } from '../graphql/tours';
import { TRACK_PAGE_VIEW } from '../graphql/analytics';
import { getVisitorSessionId } from '../lib/visitorSession';

/* ─── Design tokens ─── */
const GOLD     = '#B8975A';
const GOLD_DIM = 'rgba(184,151,90,0.15)';
const NAVY     = '#080e1a';

/* ─── Safe JSON parser ─── */
// const parseJSON = (val, fallback = []) => {
//   if (!val) return fallback;
//   if (Array.isArray(val)) return val;
//   if (typeof val === 'object') return val;
//   try { const p = JSON.parse(val); return p ?? fallback; } catch { return fallback; }
// };

const parseJSON = (val, fallback = []) => {
  if (!val) return fallback;
  if (Array.isArray(val)) return val;
  if (typeof val === 'object') return val;
  try {
    const p = JSON.parse(val);
    // If the stored value was a bare URL string, wrap it
    if (typeof p === 'string') return [p];
    return p ?? fallback;
  } catch {
    // If JSON.parse fails, val itself might be a bare URL string
    if (typeof val === 'string' && val.startsWith('http')) return [val];
    return fallback;
  }
};


/* ─── Icon registry for TourHighlight.icon_name ─── */
const ICON_MAP = {
  Camera, Compass, Leaf, Award, Sunrise, TrendingUp,
  Utensils, Navigation, Mountain, Waves, Building2,
  ShieldCheck, Star, Users, Zap,
};
const getIcon = (name) => ICON_MAP[name] || Compass;

/* ─── Language code → label ─── */
const LANG_LABELS = { en: 'English', sw: 'Swahili', fr: 'French', de: 'German', es: 'Spanish', it: 'Italian' };
const langLabel   = (c) => LANG_LABELS[c] || c.toUpperCase();

/* ─── Difficulty → bar % ─── */
const DIFF_PCT = { easy: 25, moderate: 50, challenging: 75, strenuous: 100 };

const PageStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400;1,600&family=Jost:wght@300;400;500;600&display=swap');

    .f-cormorant { font-family: 'Cormorant Garamond', serif; }
    .f-jost      { font-family: 'Jost', sans-serif; }

    .grain {
      background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E");
    }

    .gallery-thumb { cursor:pointer; overflow:hidden; border:2px solid transparent; transition:border-color .25s,opacity .25s; opacity:.55; }
    .gallery-thumb:hover { opacity:.85; }
    .gallery-thumb.active { border-color:${GOLD}; opacity:1; }
    .gallery-thumb img { width:100%; height:100%; object-fit:cover; transition:transform .5s; }
    .gallery-thumb:hover img { transform:scale(1.06); }

    .tab-btn {
      font-family:'Jost',sans-serif; font-size:13px; font-weight:600;
      letter-spacing:0.16em; text-transform:uppercase;
      padding:10px 20px; border-bottom:2px solid transparent;
      color:#12100e; transition:all .25s; white-space:nowrap;
      background:transparent; cursor:pointer;
    }
    .tab-btn:hover { color:#334155; }
    .tab-btn.active { color:${NAVY}; border-bottom-color:${GOLD}; }

    .feature-tile { padding:22px 24px; border:1px solid #e8e4de; background:#FAFAF8; transition:border-color .3s; }
    .feature-tile:hover { border-color:${GOLD}; }

    .itin-step + .itin-step { border-top:1px solid #f1ede8; margin-top:0; }

    .highlight-pill {
      font-family:'Jost',sans-serif; font-size:13px; font-weight:500;
      letter-spacing:0.14em; text-transform:uppercase;
      padding:5px 14px; border:1px solid #e2ddd6; color:#12100e; background:#fff;
    }

    .gold-rule { height:1px; background:linear-gradient(90deg,${GOLD} 0%,rgba(184,151,90,.1) 100%); }

    .reserve-btn {
      position:relative; display:flex; align-items:center; justify-content:center; gap:10px;
      width:100%; font-family:'Jost',sans-serif; font-size:13px; font-weight:600;
      letter-spacing:0.16em; text-transform:uppercase;
      padding:18px 28px; border:1px solid ${GOLD}; background:${GOLD}; color:${NAVY};
      overflow:hidden; transition:all .35s cubic-bezier(.22,1,.36,1); text-decoration:none;
    }
    .reserve-btn::before {
      content:''; position:absolute; inset:0; background:${NAVY};
      transform:scaleX(0); transform-origin:left;
      transition:transform .35s cubic-bezier(.22,1,.36,1); z-index:0;
    }
    .reserve-btn:hover::before { transform:scaleX(1); }
    .reserve-btn:hover { color:#fff; border-color:${NAVY}; }
    .reserve-btn span, .reserve-btn svg { position:relative; z-index:1; transition:color .35s; }

    .ghost-btn-sm {
      display:inline-flex; align-items:center; justify-content:center;
      width:38px; height:38px; border:1px solid rgba(255,255,255,.18);
      background:rgba(255,255,255,.07); backdrop-filter:blur(8px);
      color:rgba(255,255,255,.8); transition:all .3s; cursor:pointer;
    }
    .ghost-btn-sm:hover { border-color:rgba(255,255,255,.5); color:#fff; }
    .ghost-btn-sm.fav-active { border-color:#f87171; background:rgba(248,113,113,.15); color:#f87171; }

    @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
    .fade-up { animation:fadeUp .6s cubic-bezier(.22,1,.36,1) both; }

    .sidebar-card { border:1px solid #e8e4de; background:#fff; }

    .trust-item { display:flex; align-items:center; gap:12px; padding:14px 0; border-bottom:1px solid #f1ede8; }
    .trust-item:last-child { border-bottom:none; }
    .trust-icon { width:32px; height:32px; flex-shrink:0; display:flex; align-items:center; justify-content:center; border:1px solid #e8e4de; }

    .included-row { display:flex; align-items:flex-start; gap:12px; padding:11px 0; border-bottom:1px solid #f5f1ec; }
    .included-row:last-child { border-bottom:none; }

    .summary-stat { flex:1; padding:20px 22px; border:1px solid #e8e4de; background:#FAFAF8; text-align:center; transition:border-color .3s; }
    .summary-stat:hover { border-color:${GOLD}; }

    .diff-bar-track { height:4px; background:#ede9e3; position:relative; overflow:hidden; }
    .diff-bar-fill { position:absolute; left:0; top:0; height:100%; background:linear-gradient(90deg,${GOLD},rgba(184,151,90,.4)); transition:width .8s cubic-bezier(.22,1,.36,1); }

    .exp-block { padding:18px 20px; border:1px solid #e8e4de; background:#fff; transition:border-color .3s,transform .3s; }
    .exp-block:hover { border-color:${GOLD}; transform:translateY(-2px); }

    .hero-stat-band { background:rgba(8,14,26,.92); backdrop-filter:blur(20px); border-top:1px solid rgba(184,151,90,.18); border-bottom:1px solid rgba(184,151,90,.1); }
    .hero-stat { flex:1; padding:18px 24px; border-right:1px solid rgba(255,255,255,.06); display:flex; flex-direction:column; gap:4px; }
    .hero-stat:last-child { border-right:none; }

    .gallery-strip { display:flex; gap:6px; align-items:flex-end; }
    .gallery-strip-thumb { cursor:pointer; overflow:hidden; border:1px solid transparent; transition:border-color .25s,opacity .25s; opacity:.5; flex-shrink:0; }
    .gallery-strip-thumb:hover { opacity:.8; }
    .gallery-strip-thumb.active { border-color:${GOLD}; opacity:1; }
    .gallery-strip-thumb img { width:100%; height:100%; object-fit:cover; display:block; transition:transform .5s; }
    .gallery-strip-thumb:hover img { transform:scale(1.06); }

    .avail-dot { width:6px; height:6px; border-radius:50%; animation:pulse-dot 2s ease-in-out infinite; }
    @keyframes pulse-dot { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:.5;transform:scale(1.4)} }

    .date-chip {
      font-family:'Jost',sans-serif; font-size:13px; font-weight:500;
      letter-spacing:.15em; text-transform:uppercase;
      padding:6px 12px; border:1px solid rgba(255,255,255,.1);
      color:rgba(255,255,255,.5); cursor:pointer; transition:all .25s;
      background:transparent; white-space:nowrap;
    }
    .date-chip:hover { border-color:rgba(184,151,90,.5); color:rgba(255,255,255,.8); }
    .date-chip.sel { border-color:${GOLD}; color:${GOLD}; background:rgba(184,151,90,.1); }

    .cap-bar-track { height:3px; background:rgba(255,255,255,.08); flex:1; overflow:hidden; }
    .cap-bar-fill { height:100%; background:linear-gradient(90deg,${GOLD},rgba(184,151,90,.5)); transition:width .8s cubic-bezier(.22,1,.36,1); }

    .rtier { border:1px solid; position:relative; overflow:hidden; margin-bottom:10px; }
    .rtier-bar { position:absolute; top:0; left:0; width:3px; height:100%; }
  `}</style>
);

/* ─── Helpers ─── */
const SL = ({ text }) => (
  <div className="flex items-center gap-3 mb-3">
    <div className="w-6 h-px" style={{ background: GOLD }} />
    <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em]" style={{ color: GOLD }}>{text}</span>
  </div>
);

const Stars = ({ rating }) => (
  <div className="flex items-center gap-0.5">
    {[1,2,3,4,5].map(i => (
      <Star key={i} size={11}
        fill={i <= Math.round(rating) ? GOLD : 'none'}
        stroke={i <= Math.round(rating) ? GOLD : '#cbd5e1'}
        strokeWidth={1.5} />
    ))}
  </div>
);

/* ─── RefundTier ─── */
const RefundTier = ({ tier }) => {
  const isGreen  = tier.tierType === 'full';
  const isOrange = tier.tierType === 'partial';
  const color  = isGreen ? '#22c55e' : isOrange ? GOLD : '#ef4444';
  const bg     = isGreen ? 'rgba(34,197,94,.07)' : isOrange ? GOLD_DIM : 'rgba(239,68,68,.07)';
  const border = isGreen ? 'rgba(34,197,94,.25)' : isOrange ? 'rgba(184,151,90,.32)' : 'rgba(239,68,68,.2)';
  const Icon   = isGreen ? CheckCircle2 : isOrange ? AlertCircle : XCircle;
  return (
    <div className="rtier" style={{ borderColor: border, background: bg }}>
      <div className="rtier-bar" style={{ background: color }} />
      <div style={{ padding: '14px 14px 14px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1 }}>
            <Icon size={16} strokeWidth={1.5} style={{ color, flexShrink: 0 }} />
            <span className="f-jost" style={{ fontSize: '15px', fontWeight: 400, color: '#12100e' }}>{tier.windowLabel}</span>
          </div>
          <div style={{ background: color, padding: '4px 12px', flexShrink: 0 }}>
            <span className="f-cormorant" style={{ fontSize: '27px', fontWeight: 300, color: '#fff', lineHeight: 1 }}>{tier.refundDisplay}</span>
          </div>
        </div>
        <p className="f-jost" style={{ fontSize: '15px', fontWeight: 300, color: '#12100e', lineHeight: 1.65, paddingLeft: '26px' }}>{tier.description}</p>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   SUMMARY TAB
═══════════════════════════════════════════ */
const TourSummaryTab = ({ tour }) => {
  const includes  = parseJSON(tour?.includes);
  const excludes  = parseJSON(tour?.excludes);
  const diffPct   = DIFF_PCT[tour.difficultyLevel] || 50;
  const diffLabel = tour.difficultyLabel || 'Moderate';
  const languages = (tour.guideLanguages || []).map(langLabel);
  const price     = Math.round(tour.currentPrice || tour.agencyServiceFee || 0);

  // All four Quick Facts now from DB
  const quickFacts = [
    { icon: Sunrise,    label: 'Best Season', value: tour.recommendedBestSeason || '—'                        },
    { icon: TrendingUp, label: 'Difficulty',  value: (tour.difficultyLabel || 'Moderate').split(' ')[0]       },
    { icon: Award,      label: 'Guide Tier',  value: tour.guideTierLabel || 'Expert+'                         },
    { icon: Users,      label: 'Minimum Age', value: tour.minAge != null ? `${tour.minAge}+` : 'All ages'     },
  ];

  return (
    <div className="p-8 sm:p-12">
      <SL text="At a Glance" />
      <h2 className="f-cormorant text-slate-900 font-light mb-2" style={{ fontSize: '36px' }}>Tour Summary</h2>
      <p className="f-jost text-[15px] text-slate-400 font-light mb-10 leading-relaxed">
        Everything you need to know before you book — distilled into one clear overview.
      </p>

      {/* Stats row */}
      <div className="flex flex-wrap gap-px mb-10" style={{ background: '#e8e4de' }}>
        {[
          { val: tour.durationLabel || `${tour.durationDays} Days`, label: 'Duration'                        },
          { val: `Max ${tour.maxParticipants}`,                     label: 'Group Size'                      },
          { val: `$${price.toLocaleString()}`,                      label: 'Per Person'                      },
          { val: `${tour.averageRating} ★`,                         label: `${tour.totalReviews} Reviews`    },
        ].map(({ val, label }) => (
          <div key={label} className="summary-stat" style={{ minWidth: '120px' }}>
            <p className="f-cormorant text-[30px] font-light text-slate-900 leading-none mb-1">{val}</p>
            <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400">{label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mb-10">

        {/* Included / Excluded */}
        <div>
          <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-5" style={{ color: GOLD }}>What's Included</p>
          <div>
            {includes.map((text, i) => (
              <div key={i} className="included-row">
                <CheckCircle2 size={14} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0, marginTop: 1 }} />
                <span className="f-jost text-[15px] font-light leading-snug text-slate-700">{text}</span>
              </div>
            ))}
            {excludes.map((text, i) => (
              <div key={i} className="included-row">
                <XCircle size={14} strokeWidth={1.5} style={{ color: '#6b7280', flexShrink: 0, marginTop: 1 }} />
                <span className="f-jost text-[15px] font-light leading-snug text-slate-400 line-through decoration-slate-300">{text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-8">

          {/* Difficulty */}
          <div>
            <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-4" style={{ color: GOLD }}>Physical Difficulty</p>
            <div className="flex items-center gap-4 mb-2">
              <span className="f-cormorant text-[27px] text-slate-800 font-light">{diffLabel}</span>
            </div>
            <div className="diff-bar-track mb-2">
              <div className="diff-bar-fill" style={{ width: `${diffPct}%` }} />
            </div>
            <div className="flex justify-between">
              {['Easy', 'Moderate', 'Demanding', 'Strenuous'].map((lvl, i) => (
                <span key={lvl} className="f-jost text-[12px] uppercase tracking-[0.15em]"
                  style={{ color: i === ['easy','moderate','challenging','strenuous'].indexOf(tour.difficultyLevel) ? GOLD : '#cbd5e1' }}>
                  {lvl}
                </span>
              ))}
            </div>
          </div>

          {/* Languages */}
          {languages.length > 0 && (
            <div>
              <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>Available Languages</p>
              <div className="flex flex-wrap gap-2">
                {languages.map(lang => <span key={lang} className="highlight-pill">{lang}</span>)}
              </div>
            </div>
          )}

          {/* Quick Facts — from DB */}
          <div>
            <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>Quick Facts</p>
            <div className="grid grid-cols-2 gap-2">
              {quickFacts.map(({ icon: Icon, label, value }) => (
                <div key={label} className="exp-block">
                  <Icon size={13} strokeWidth={1.5} className="mb-2" style={{ color: GOLD }} />
                  <p className="f-jost text-[12px] uppercase tracking-[0.16em] text-slate-400 mb-0.5">{label}</p>
                  <p className="f-cormorant text-[18px] text-slate-800 font-light">{value}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Cancellation — original strip design, description from DB */}
      <div className="gold-rule mb-6" />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 p-6 mb-8"
        style={{ background: GOLD_DIM, border: '1px solid rgba(184,151,90,0.25)' }}>
        <div className="flex items-start gap-4">
          <ShieldCheck size={18} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0, marginTop: 2 }} />
          <div>
            <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] mb-1" style={{ color: GOLD }}>Cancellation Policy</p>
            <p className="f-jost text-[15px] text-slate-600 font-light leading-relaxed">
              {tour.cancellationPolicy?.description || (
                <>Full refund available up to <strong className="font-semibold text-slate-800">30 days</strong> before departure. 50% refund between 30–14 days. Cancellations within 14 days are non-refundable. Date transfers always free.</>
              )}
            </p>
          </div>
        </div>
        <Link to="/cancellation-policy"
          className="flex-shrink-0 f-jost text-[13px] font-semibold uppercase tracking-[0.16em] flex items-center gap-2 transition-all"
          style={{ color: GOLD, textDecoration: 'none' }}>
          <span>Full Policy</span>
          <ArrowRight size={10} strokeWidth={2} />
        </Link>
      </div>

      {/* Full package CTA */}
      <div className="relative overflow-hidden p-8 sm:p-10 dark-sec" style={{ background: NAVY, border: '1px solid rgba(184,151,90,0.2)' }}>
        <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: `linear-gradient(90deg,${GOLD},rgba(184,151,90,.1))` }} />
        <div className="grain absolute inset-0 pointer-events-none opacity-40" />
        <div className="relative z-10 flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>Ready for the Full Picture?</p>
            <h3 className="f-cormorant text-white font-light leading-snug mb-2" style={{ fontSize: '30px' }}>View the Complete Tour Package</h3>
            <p className="f-jost text-[15px] font-light leading-relaxed" style={{ color: 'rgba(255,255,255,.45)', maxWidth: '380px' }}>
              Full day-by-day programme, guide bios, interactive route map, guest reviews, and exclusive add-ons.
            </p>
          </div>
          <Link to={`/tours/${tour.slug}/package`}
            className="flex-shrink-0 inline-flex items-center gap-3 px-8 py-4"
            style={{ border: `1px solid ${GOLD}`, color: GOLD, background: 'transparent', textDecoration: 'none', transition: 'background .3s,color .3s' }}
            onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = NAVY; }}
            onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = GOLD; }}>
            <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em]">Full Tour Package</span>
            <ArrowRight size={12} strokeWidth={2} />
          </Link>
        </div>
      </div>
    </div>
  );
};
/* ═══════════════════════════════════════════
   LOGISTICS TAB
   All sections driven from DB. Each section
   gracefully omits itself when data is absent.
═══════════════════════════════════════════ */
const LogisticsTab = ({ tour }) => {
  const agency            = tour.agency || {};
  const preDepartureSteps = parseJSON(tour.preDepartureSteps).map(s => typeof s === 'string' ? JSON.parse(s) : s);
  const climateMonths     = parseJSON(tour.climateMonths).map(s => typeof s === 'string' ? JSON.parse(s) : s);
  const packingCategories = parseJSON(tour.packingCategories).map(s => typeof s === 'string' ? JSON.parse(s) : s);
  const healthRequirements = parseJSON(tour.healthRequirements).map(s => typeof s === 'string' ? JSON.parse(s) : s);

  /* Derive airport label: prefer DB fields, fall back to park countries */
  const airportCode  = tour.arrivalAirport || '';
  const airportName  = tour.arrivalAirportName || '';
  const hasArrival   = airportCode || airportName || tour.meetingPoint;

  /* Contact rows — merge agency DB fields with sensible fallbacks */
  const contactRows = [
    // Line ~560 in the logistics contact rows, replace:
    { icon: Phone, label: 'Concierge Hotline',    value: tour.agency?.phone || '+255 (0) 27 254 8000',  sub: '24h · English & Swahili' },
    { icon: Mail,  label: 'Email our team',      value: tour.agency?.primaryEmail || 'safari@voleluxe.co.tz', sub: 'Response within 4 hours' },
    { icon: Phone, label: 'Emergency Field Line',  value: tour.agency?.emergencyPhone || '+255 (0) 754 000 111', sub: 'In-country · 24h' },
    { icon: Mail,  label: 'Pre-Departure Queries', value: tour.agency?.preDepartureEmail || 'plan@voleluxe.co.tz', sub: 'Kit, visa, health advice' },
  ].filter(r => r.value); // only render rows where a value exists

  return (
    <div className="p-8 sm:p-12">
      <SL text="Before You Arrive" />
      <h2 className="f-cormorant text-slate-900 font-light mb-2" style={{ fontSize: '36px' }}>
        Everything You Need to Know
      </h2>
      <p className="f-jost text-[15px] text-slate-400 font-light mb-10 leading-relaxed">
        A complete pre-departure guide — from landing at the airport to what to pack in your kit bag.
      </p>

      {/* ── 1. Arrival & Departure ─────────────────────────────── */}
      {hasArrival && (
        <>
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <div style={{ width: '32px', height: '32px', border: '1px solid rgba(184,151,90,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: GOLD_DIM }}>
                <Navigation size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <h3 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px' }}>Arrival & Departure</h3>
            </div>
            <div style={{ border: '1px solid #e8e4de', background: '#FAFAF8', marginBottom: '12px' }}>
              <div style={{ height: '2px', background: `linear-gradient(90deg,${GOLD},rgba(184,151,90,.06))` }} />
              <div style={{ padding: '24px 28px' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px', flexWrap: 'wrap' }}>
                  {(airportCode || airportName) && (
                    <div>
                      <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>
                        Recommended Gateway
                      </p>
                      <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '22px', marginBottom: '6px' }}>
                        {airportName}{airportCode ? ` (${airportCode})` : ''}
                      </p>
                      {tour.arrivalTransferNotes && (
                        <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">
                          {tour.arrivalTransferNotes}
                        </p>
                      )}
                    </div>
                  )}
                  <div>
                    <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-3" style={{ color: GOLD }}>Arrival Day</p>
                    <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '22px', marginBottom: '6px' }}>
                      Day 1 — No rush
                    </p>
                    <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">
                      Arrive any time on Day 1. Your first full day of activities begins on Day 2.
                    </p>
                  </div>
                </div>
                {tour.meetingPoint && (
                  <>
                    <div className="gold-rule" style={{ margin: '20px 0' }} />
                    <div>
                      <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-2" style={{ color: GOLD }}>
                        Meeting Point
                      </p>
                      <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '25px', marginBottom: '6px' }}>
                        {tour.meetingPoint}
                      </p>
                      {tour.meetingPointNotes && (
                        <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">
                          {tour.meetingPointNotes}
                        </p>
                      )}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="gold-rule" style={{ marginBottom: '36px' }} />
        </>
      )}

      {/* ── 2. Pre-Departure Timeline ──────────────────────────── */}
      {preDepartureSteps.length > 0 && (
        <>
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <div style={{ width: '32px', height: '32px', border: '1px solid rgba(184,151,90,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: GOLD_DIM }}>
                <Calendar size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <h3 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px' }}>Pre-Departure Timeline</h3>
            </div>
            <div style={{ borderLeft: '1px solid #e8e4de', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '0' }}>
              {preDepartureSteps.map((step, i) => (
                <div key={i} style={{ paddingBottom: '20px', paddingTop: i === 0 ? '0' : '20px', borderBottom: i < preDepartureSteps.length - 1 ? '1px solid #f5f2ee' : 'none', position: 'relative' }}>
                  <div style={{ position: 'absolute', left: '-30px', top: i === 0 ? '4px' : '24px', width: '12px', height: '12px', border: `1px solid ${GOLD}`, background: '#F5F3EF', transform: 'rotate(45deg)' }} />
                  <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] mb-1" style={{ color: GOLD }}>{step.when}</p>
                  <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '22px', marginBottom: '5px' }}>{step.title}</p>
                  <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">{step.body}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="gold-rule" style={{ marginBottom: '36px' }} />
        </>
      )}

      {/* ── 3. Climate & Best Season ───────────────────────────── */}
      {climateMonths.length > 0 && (
        <>
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <div style={{ width: '32px', height: '32px', border: '1px solid rgba(184,151,90,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: GOLD_DIM }}>
                <Sunrise size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <h3 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px' }}>Climate & Best Season</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: '8px', marginBottom: '16px' }}>
              {climateMonths.map((m, i) => (
                <div key={i} style={{ padding: '16px 18px', border: `1px solid ${m.highlight ? 'rgba(184,151,90,.5)' : '#e8e4de'}`, background: m.highlight ? GOLD_DIM : '#FAFAF8', position: 'relative' }}>
                  {m.highlight && <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: GOLD }} />}
                  <p className="f-jost text-[12px] font-semibold uppercase tracking-[0.16em] mb-1" style={{ color: m.highlight ? GOLD : '#94a3b8' }}>{m.season}</p>
                  <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '18px', marginBottom: '2px' }}>{m.label}</p>
                  <p className="f-jost text-[13px] text-slate-400 font-light">{m.temp}{m.note ? ` · ${m.note}` : ''}</p>
                </div>
              ))}
            </div>
            {agency.climateRecommendation && (
              <p className="f-jost text-[15px] text-slate-400 font-light leading-relaxed" style={{ padding: '12px 16px', background: '#f9f7f4', border: '1px solid #f0ece6' }}>
                <span style={{ color: GOLD, fontWeight: 600 }}>Our recommendation:</span>{' '}
                {agency.climateRecommendation}
              </p>
            )}
          </div>
          <div className="gold-rule" style={{ marginBottom: '36px' }} />
        </>
      )}

      {/* ── 4. What to Pack ────────────────────────────────────── */}
      {packingCategories.length > 0 && (
        <>
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <div style={{ width: '32px', height: '32px', border: '1px solid rgba(184,151,90,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: GOLD_DIM }}>
                <Award size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <h3 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px' }}>What to Pack</h3>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {packingCategories.map((cat, ci) => (
                <div key={ci} style={{ border: '1px solid #e8e4de', background: '#FAFAF8', padding: '20px 22px' }}>
                  <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] mb-3" style={{ color: GOLD }}>{cat.category}</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
                    {(cat.items || []).map((item, ii) => (
                      <div key={ii} style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', padding: '6px 0', borderBottom: ii < cat.items.length - 1 ? '1px solid #f0ece6' : 'none' }}>
                        <div style={{ width: '5px', height: '5px', background: GOLD, borderRadius: '50%', flexShrink: 0, marginTop: '5px' }} />
                        <span className="f-jost text-[15px] text-slate-500 font-light leading-snug">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="gold-rule" style={{ marginBottom: '36px' }} />
        </>
      )}

      {/* ── 5. Health, Visa & Entry ────────────────────────────── */}
      {healthRequirements.length > 0 && (
        <>
          <div style={{ marginBottom: '40px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
              <div style={{ width: '32px', height: '32px', border: '1px solid rgba(184,151,90,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: GOLD_DIM }}>
                <ShieldCheck size={14} strokeWidth={1.5} style={{ color: GOLD }} />
              </div>
              <h3 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px' }}>Health, Visa & Entry</h3>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {healthRequirements.map((req, i) => {
                const color = req.status === 'required' ? GOLD : req.status === 'recommended' ? '#64748b' : '#94a3b8';
                const bg    = req.status === 'required' ? GOLD_DIM : '#FAFAF8';
                return (
                  <div key={i} style={{ display: 'flex', gap: '16px', padding: '16px 20px', border: `1px solid ${req.status === 'required' ? 'rgba(184,151,90,.3)' : '#e8e4de'}`, background: bg, alignItems: 'flex-start' }}>
                    <div style={{ flexShrink: 0, paddingTop: '1px' }}>
                      <span className="f-jost text-[12px] font-semibold uppercase tracking-[0.14em]" style={{ color, padding: '2px 8px', border: `1px solid ${color}`, background: 'rgba(255,255,255,.6)', whiteSpace: 'nowrap' }}>
                        {req.status}
                      </span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.14em] mb-1" style={{ color: '#12100e' }}>{req.label}</p>
                      <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">{req.detail}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="gold-rule" style={{ marginBottom: '36px' }} />
        </>
      )}

      {/* ── 6. Contact ─────────────────────────────────────────── */}
      {contactRows.length > 0 && (
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '18px' }}>
            <div style={{ width: '32px', height: '32px', border: '1px solid rgba(184,151,90,.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, background: GOLD_DIM }}>
              <Phone size={14} strokeWidth={1.5} style={{ color: GOLD }} />
            </div>
            <h3 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px' }}>Talk to Our Safari Team</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {contactRows.map(({ icon: Icon, label, value, sub }) => (
              <div key={label} className="feature-tile" style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
                <div className="trust-icon flex-shrink-0" style={{ borderColor: GOLD_DIM }}>
                  <Icon size={14} strokeWidth={1.5} style={{ color: GOLD }} />
                </div>
                <div>
                  <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400 mb-1">{label}</p>
                  <p className="f-cormorant text-slate-800 font-light" style={{ fontSize: '21px', marginBottom: '2px' }}>{value}</p>
                  <p className="f-jost text-[13px] text-slate-400 font-light">{sub}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════
   TOUR DETAILS — main component
═══════════════════════════════════════════ */
export default function TourDetails() {
  const { id: slug }        = useParams();
  const [imgIdx,        setImgIdx]        = useState(0);
  const [isFavorite,    setIsFavorite]    = useState(false);
  const [activeTab,     setActiveTab]     = useState('overview');
  const [selectedDate,  setSelectedDate]  = useState(0);

  const { data, loading, error } = useQuery(GET_TOUR_DETAILS, {
    variables: { slug },
    fetchPolicy: 'cache-first',
  });

  const tour = data?.tour;

  const [trackPageView] = useMutation(TRACK_PAGE_VIEW);

  // One view per tour actually reached, not per query — the tour id only
  // exists once GET_TOUR_DETAILS resolves, and re-firing on every
  // cache-first re-render would count a single visit many times over.
  useEffect(() => {
    if (!tour?.id) return;
    trackPageView({
      variables: {
        pageUrl: window.location.pathname,
        sessionId: getVisitorSessionId(),
        pageTitle: tour.name,
        tourId: tour.id,
      },
    }).catch(() => {
      // Tracking must never block or break the page it's tracking.
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tour?.id]);

  const images = tour
    ? (parseJSON(tour.galleryImages).length > 0
        ? parseJSON(tour.galleryImages)
        : [tour.featuredImage].filter(Boolean))
    : [];

  useEffect(() => {
    if (images.length > 1) {
      const t = setInterval(() => setImgIdx(i => (i + 1) % images.length), 4500);
      return () => clearInterval(t);
    }
  }, [images.length]);

  if (loading) return (
    <div className="f-jost min-h-screen flex items-center justify-center" style={{ background: '#F5F3EF' }}>
      <PageStyles />
      <p className="f-jost text-[15px] uppercase tracking-[0.16em] text-slate-400">Loading safari…</p>
    </div>
  );

  if (error || !tour) return (
    <div className="f-jost min-h-screen flex items-center justify-center" style={{ background: '#F5F3EF' }}>
      <PageStyles />
      <p className="f-jost text-[15px] text-red-400">Tour not found.</p>
    </div>
  );

  const price             = Math.round(tour.currentPrice || tour.agencyServiceFee || 0);
  const guideAssignments  = tour.guideAssignments  || [];
  const primaryAssignment = guideAssignments.find(a => a.isPrimary) || guideAssignments[0];
  const primaryGuide      = primaryAssignment?.guide;
  const languages         = (tour.guideLanguages || []).map(langLabel);
  const departures        = tour.upcomingDepartures || [];
  const selDep            = departures[selectedDate];
  const differentiators   = parseJSON(tour.differentiators).map(d => typeof d === 'string' ? JSON.parse(d) : d);
  const location          = (tour.parks || []).map(p => p.country?.name).filter(Boolean).filter((v,i,a)=>a.indexOf(v)===i).join(', ') || 'Tanzania';

  const tabs = ['overview', 'summary', 'itinerary', 'logistics'];

  return (
    <div className="f-jost min-h-screen" style={{ background: '#F5F3EF' }}>
      <PageStyles />

      {/* ══════════════════════════════════════
          HERO GALLERY
      ══════════════════════════════════════ */}
      <div className="dark-sec" style={{ position: 'relative', background: NAVY }}>
        <div style={{ position: 'relative', height: '88vh', minHeight: '580px', overflow: 'hidden' }}>
          {images.map((src, i) => (
            <img key={src} src={src} alt={tour.name}
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover',
                opacity: i === imgIdx ? 0.52 : 0, transition: 'opacity 1.2s ease' }} />
          ))}
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to bottom,rgba(8,14,26,.55) 0%,transparent 28%,rgba(8,14,26,.78) 100%)' }} />
          <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to right,rgba(8,14,26,.45) 0%,transparent 60%)' }} />
          <div className="grain" style={{ position: 'absolute', inset: 0, pointerEvents: 'none', opacity: .55 }} />
          <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '-10%', left: '60%', width: '1px', height: '130%', background: 'linear-gradient(to bottom,transparent,rgba(184,151,90,.18) 30%,rgba(184,151,90,.10) 70%,transparent)', transform: 'rotate(-6deg)', transformOrigin: 'top' }} />
          </div>

          <div style={{ position: 'absolute', top: 0, left: 0, right: 0, padding: '73px 32px 28px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', zIndex: 20 }}>
            <Link to="/tours" className="ghost-btn-sm" style={{ width: 'auto', padding: '9px 16px', display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
              <ChevronLeft size={14} strokeWidth={1.5} />
              <span className="f-jost" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.16em' }}>All safaris</span>
            </Link>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="ghost-btn-sm"><Share2 size={15} strokeWidth={1.5} /></button>
              <button onClick={() => setIsFavorite(!isFavorite)} className={`ghost-btn-sm ${isFavorite ? 'fav-active' : ''}`}>
                <Heart size={15} strokeWidth={1.5} fill={isFavorite ? 'currentColor' : 'none'} />
              </button>
            </div>
          </div>

          <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '0 32px 36px', zIndex: 20 }}>
            <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: '24px' }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '16px', flexWrap: 'wrap' }}>
                  <span className="f-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: GOLD }}>
                    {tour.category?.name || 'Safari'}
                  </span>
                  <div style={{ width: '1px', height: '12px', background: 'rgba(255,255,255,.2)' }} />
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Stars rating={tour.averageRating} />
                    <span className="f-jost" style={{ fontSize: '13px', color: 'rgba(255,255,255,.5)', letterSpacing: '.04em' }}>
                      {tour.averageRating} · {tour.totalReviews} reviews
                    </span>
                  </div>
                </div>
                <h1 className="f-cormorant" style={{ fontSize: 'clamp(46px,6vw,78px)', fontWeight: 300, fontStyle: 'normal', color: '#fff', lineHeight: .92, marginBottom: '20px', maxWidth: '720px' }}>
                  {tour.name}
                </h1>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '28px' }}>
                  {[
                    { I: MapPin,     v: location },
                    { I: Clock,      v: tour.durationLabel || `${tour.durationDays} Days` },
                    { I: Users,      v: `Max ${tour.maxParticipants} Guests` },
                    { I: TrendingUp, v: tour.difficultyLabel || 'Moderate · Active' },
                  ].map(({ I, v }) => (
                    <div key={v} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <I size={12} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                      <span className="f-jost" style={{ fontSize: '15px', color: 'rgba(255,255,255,.7)', fontWeight: 300 }}>{v}</span>
                    </div>
                  ))}
                </div>
                <div className="gallery-strip hidden lg:flex">
                  {images.slice(0, 5).map((img, i) => (
                    <div key={i} onClick={() => setImgIdx(i)}
                      className={`gallery-strip-thumb ${imgIdx === i ? 'active' : ''}`}
                      style={{ width: '72px', height: '48px' }}>
                      <img src={img} alt={`View ${i + 1}`} />
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', paddingLeft: '10px' }}>
                    <span className="f-jost" style={{ fontSize: '12px', color: 'rgba(255,255,255,.3)', letterSpacing: '0.16em', textTransform: 'uppercase' }}>{images.length} {images.length === 1 ? 'Photo' : 'Photos'}</span>
                  </div>
                </div>
              </div>

              <div className="hidden lg:block" style={{ flexShrink: 0, background: 'rgba(8,14,26,.75)', backdropFilter: 'blur(20px)', border: '1px solid rgba(184,151,90,.25)', padding: '24px 28px', minWidth: '220px' }}>
                <p className="f-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,151,90,.7)', marginBottom: '6px' }}>From</p>
                <p className="f-cormorant" style={{ fontSize: '46px', fontWeight: 300, color: '#fff', lineHeight: 1, marginBottom: '4px' }}>${price.toLocaleString()}</p>
                <p className="f-jost" style={{ fontSize: '13px', fontStyle: 'normal', color: 'rgba(255,255,255,.35)', marginBottom: '16px' }}>USD per person</p>
                <div style={{ height: '1px', background: 'rgba(184,151,90,.2)', marginBottom: '16px' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: '7px' }}>
                  <div className="avail-dot" style={{ background: departures.length > 0 ? '#4ade80' : '#94a3b8' }} />
                  <span className="f-jost" style={{ fontSize: '13px', color: 'rgba(255,255,255,.5)', letterSpacing: '.15em' }}>
                    {departures.length > 0 ? 'Spaces available' : 'Check availability'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="hero-stat-band">
          <div style={{ maxWidth: '1600px', margin: '0 auto', display: 'flex', overflowX: 'auto' }}>
            {[
              { label: 'Duration',   value: tour.durationLabel || `${tour.durationDays} Days`, sub: 'Full safari days' },
              { label: 'Group Size', value: `Max ${tour.maxParticipants}`,                     sub: 'Private vehicle always' },
              { label: 'From',       value: `$${price.toLocaleString()}`,                      sub: 'Per person, full board' },
              { label: 'Parks',      value: `${(tour.parks||[]).length} Parks`,                sub: (tour.parks||[]).slice(0,2).map(p=>p.name).join(', ') },
              { label: 'Rating',     value: `${tour.averageRating} ★`,                         sub: `${tour.totalReviews} verified reviews` },
            ].map(({ label, value, sub }) => (
              <div key={label} className="hero-stat">
                <p className="f-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,151,90,.65)' }}>{label}</p>
                <p className="f-cormorant" style={{ fontSize: '27px', fontWeight: 300, color: '#fff', lineHeight: 1 }}>{value}</p>
                <p className="f-jost" style={{ fontSize: '13px', color: 'rgba(255,255,255,.28)', fontWeight: 300, letterSpacing: '.05em' }}>{sub}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════
          CONTENT GRID
      ══════════════════════════════════════ */}
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 py-12 lg:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start">

          {/* MAIN COLUMN */}
          <div className="lg:col-span-8 space-y-8">

            <p className="f-cormorant text-slate-600 text-[25px] leading-relaxed font-light border-l-2 pl-5" style={{ borderColor: GOLD }}>
              "{tour.tagline}"
            </p>

            <div className="sticky z-40 border-b" style={{ top: 0, background: 'rgba(245,243,239,.95)', backdropFilter: 'blur(12px)', borderColor: '#e2ddd6' }}>
              <div className="flex gap-1 overflow-x-auto">
                {tabs.map(tab => (
                  <button key={tab} onClick={() => setActiveTab(tab)} className={`tab-btn ${activeTab === tab ? 'active' : ''}`}>{tab}</button>
                ))}
              </div>
            </div>

            <div className="sidebar-card" style={{ background: '#fff', border: '1px solid #e8e4de' }}>

              {/* ── OVERVIEW ── */}
              {activeTab === 'overview' && (
                <div className="p-8 sm:p-12">
                  <SL text="The Safari" />
                  <h2 className="f-cormorant text-slate-900 font-light mb-6" style={{ fontSize: '36px' }}>What Awaits You</h2>

                  <div style={{ borderLeft: `2px solid ${GOLD}`, paddingLeft: '20px', marginBottom: '28px' }}>
                    <p className="f-cormorant text-slate-600 font-light leading-relaxed" style={{ fontSize: '25px' }}>"{tour.tagline}"</p>
                  </div>

                  <p className="f-jost text-slate-600 text-[16px] font-light leading-[1.95] mb-10">{tour.description}</p>

                  <div className="gold-rule mb-10" />

                  {(tour.highlightAssignments || []).length > 0 && (
                    <>
                      <SL text="What You Will Experience" />
                      <h3 className="f-cormorant text-slate-900 font-light mb-6" style={{ fontSize: '30px' }}>
                        {tour.highlightAssignments.length} Reasons This Safari is Different
                      </h3>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-12">
                        {tour.highlightAssignments.map(({ highlight }) => {
                          const Icon = getIcon(highlight.iconName);
                          return (
                            <div key={highlight.id} className="feature-tile flex items-start gap-4">
                              <div style={{ width: '36px', height: '36px', flexShrink: 0, border: '1px solid rgba(184,151,90,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(184,151,90,.06)' }}>
                                <Icon size={15} strokeWidth={1.5} style={{ color: GOLD }} />
                              </div>
                              <div>
                                <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] mb-1" style={{ color: GOLD }}>{highlight.label}</p>
                                <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed">{highlight.description}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      <div className="gold-rule mb-10" />
                    </>
                  )}

                  {differentiators.length > 0 && (
                    <>
                      <SL text="Our Philosophy" />
                      <h3 className="f-cormorant text-slate-900 font-light mb-8" style={{ fontSize: '30px' }}>The Standard We Hold Ourselves To</h3>
                      <div className="space-y-0 mb-12" style={{ borderLeft: '1px solid #f0ece6' }}>
                        {differentiators.map((item, i) => (
                          <div key={i} style={{ paddingLeft: '24px', paddingBottom: '28px', paddingTop: i === 0 ? '0' : '28px', borderBottom: i < differentiators.length - 1 ? '1px solid #f5f2ee' : 'none' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                              <div style={{ width: '6px', height: '6px', background: GOLD, transform: 'rotate(45deg)', flexShrink: 0 }} />
                              <h4 className="f-cormorant text-slate-900 font-light" style={{ fontSize: '25px' }}>{item.title}</h4>
                            </div>
                            <p className="f-jost text-[15px] text-slate-500 font-light leading-[1.9]">{item.body}</p>
                          </div>
                        ))}
                      </div>
                      <div className="gold-rule mb-10" />
                    </>
                  )}

                  {primaryGuide && (
                    <>
                      <SL text="Your Lead Guide" />
                      <h3 className="f-cormorant text-slate-900 font-light mb-6" style={{ fontSize: '30px' }}>The Person Who Makes It Possible</h3>
                      <div style={{ border: '1px solid #e8e4de', background: '#FAFAF8', padding: '28px', display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '40px' }}>
                        {primaryGuide.photo && (
                          <div style={{ width: '80px', height: '80px', flexShrink: 0, overflow: 'hidden', border: `2px solid ${GOLD}` }}>
                            <img src={primaryGuide.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          </div>
                        )}
                        <div style={{ flex: 1, minWidth: '200px' }}>
                          <p className="f-cormorant text-slate-900 font-light" style={{ fontSize: '27px', marginBottom: '2px' }}>
                            {primaryGuide.user?.fullName || `${primaryGuide.user?.firstName} ${primaryGuide.user?.lastName}`}
                          </p>
                          <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em] mb-5" style={{ color: GOLD }}>
                            {primaryAssignment?.roleDisplay} · {primaryGuide.experienceYears} Years in the Field
                          </p>
                          <p className="f-jost text-[15px] text-slate-500 font-light leading-[1.9] mb-6">{primaryGuide.bio || primaryGuide.shortBio}</p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            {(primaryGuide.certifications || []).map((c, i) => (
                              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CheckCircle2 size={12} strokeWidth={1.5} style={{ color: GOLD, flexShrink: 0 }} />
                                <span className="f-jost text-[13px] text-slate-500 font-light">{c}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>

                      {tour.pullQuote && (
                        <div style={{ background: NAVY, border: '1px solid rgba(184,151,90,.2)', padding: '32px 36px', position: 'relative', overflow: 'hidden', marginBottom: '40px' }}>
                          <div style={{ height: '2px', background: `linear-gradient(90deg,${GOLD},rgba(184,151,90,.08))`, position: 'absolute', top: 0, left: 0, right: 0 }} />
                          <div style={{ position: 'absolute', top: '24px', right: '28px', opacity: .07 }}>
                            <Quote size={64} style={{ color: GOLD }} />
                          </div>
                          <div style={{ display: 'flex', gap: '6px', marginBottom: '16px' }}>
                            {[1,2,3,4,5].map(i => <Star key={i} size={12} fill={GOLD} stroke={GOLD} strokeWidth={1} />)}
                          </div>
                          <p className="f-cormorant font-light leading-relaxed" style={{ fontSize: '25px', color: '#fff', marginBottom: '20px', maxWidth: '620px', position: 'relative', zIndex: 1 }}>
                            "{tour.pullQuote}"
                          </p>
                          {tour.pullQuoteBy && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{ width: '24px', height: '1px', background: GOLD }} />
                              <p className="f-jost font-medium" style={{ fontSize: '13px', color: 'rgba(255,255,255,.8)', letterSpacing: '.1em' }}>{tour.pullQuoteBy}</p>
                            </div>
                          )}
                        </div>
                      )}
                      <div className="gold-rule mb-10" />
                    </>
                  )}

                  <SL text="At a Glance" />
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div className="feature-tile">
                      <Users size={16} strokeWidth={1.5} className="mb-3" style={{ color: GOLD }} />
                      <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400 mb-1">Group Size</p>
                      <p className="f-cormorant text-[22px] text-slate-800 font-light">Max {tour.maxParticipants} Guests</p>
                    </div>
                    <div className="feature-tile">
                      <Languages size={16} strokeWidth={1.5} className="mb-3" style={{ color: GOLD }} />
                      <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400 mb-1">Languages</p>
                      <p className="f-cormorant text-[22px] text-slate-800 font-light">{languages.join(' · ') || 'English · Swahili'}</p>
                    </div>
                    <div className="feature-tile">
                      <ShieldCheck size={16} strokeWidth={1.5} className="mb-3" style={{ color: GOLD }} />
                      <p className="f-jost text-[13px] uppercase tracking-[0.16em] text-slate-400 mb-1">Led by</p>
                      <p className="f-cormorant text-[22px] text-slate-800 font-light">
                        {tour.guideTierLabel ? `${tour.guideTierLabel} Naturalists` : 'Expert Field Naturalists'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* ── SUMMARY ── */}
              {activeTab === 'summary' && <TourSummaryTab tour={tour} />}

              {/* ── ITINERARY ── */}
              {activeTab === 'itinerary' && (
                <div className="p-8 sm:p-12">
                  <SL text="Step by Step" />
                  <h2 className="f-cormorant text-slate-900 font-light mb-10" style={{ fontSize: '36px' }}>Your Journey Unfolds</h2>

                  <div className="relative">
                    <div className="absolute left-4 top-6 bottom-6 w-px hidden sm:block" style={{ background: `linear-gradient(to bottom,${GOLD},rgba(184,151,90,.1))` }} />
                    {(tour.itineraryDays || []).map((day, i) => (
                      <div key={day.id} className="itin-step flex gap-6 py-10">
                        <div className="hidden sm:flex flex-col items-center flex-shrink-0" style={{ width: '32px' }}>
                          <div className="w-8 h-8 flex items-center justify-center border z-10" style={{ borderColor: GOLD, background: '#F5F3EF' }}>
                            <span className="f-cormorant" style={{ fontSize: '15px', color: GOLD, fontWeight: 300 }}>{day.dayNumber}</span>
                          </div>
                        </div>
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-3 mb-2">
                            <span className="f-jost text-[13px] font-semibold uppercase tracking-[0.16em]" style={{ color: GOLD }}>{day.timeLabel}</span>
                            <div className="w-px h-3 bg-slate-200" />
                            <span className="f-jost text-[13px] text-slate-400">{day.durationLabel}</span>
                            {day.locationLabel && <>
                              <div className="w-px h-3 bg-slate-200 hidden sm:block" />
                              <span className="f-jost text-[13px] text-slate-400 hidden sm:block">{day.locationLabel}</span>
                            </>}
                          </div>
                          <h3 className="f-cormorant text-[27px] text-slate-900 font-light leading-snug mb-3">{day.title}</h3>
                          <p className="f-jost text-[15px] text-slate-500 font-light leading-relaxed mb-5">{day.description}</p>
                          <div className="flex flex-wrap gap-2">
                            {parseJSON(day.includesAtStop).map(inc => (
                              <span key={inc} className="highlight-pill">{inc}</span>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-10 pt-8 border-t flex flex-col sm:flex-row sm:items-center justify-between gap-4" style={{ borderColor: '#f0ece6' }}>
                    <div>
                      <p className="f-jost text-[13px] font-semibold uppercase tracking-[0.2em] mb-1" style={{ color: GOLD }}>Want the full picture?</p>
                      <p className="f-jost text-[15px] text-slate-500 font-light">View stop-by-stop timings, insider tips, and an interactive route map.</p>
                    </div>
                    <Link to={`/tours/${tour.slug}/itinerary`}
                      className="flex-shrink-0 inline-flex items-center gap-3 f-jost text-[13px] font-semibold uppercase tracking-[0.16em] px-6 py-3 transition-all"
                      style={{ border: `1px solid ${GOLD}`, color: GOLD, background: GOLD_DIM, textDecoration: 'none' }}
                      onMouseEnter={e => { e.currentTarget.style.background = GOLD; e.currentTarget.style.color = NAVY; }}
                      onMouseLeave={e => { e.currentTarget.style.background = GOLD_DIM; e.currentTarget.style.color = GOLD; }}>
                      <span>View Detailed Itinerary</span>
                      <ArrowRight size={11} strokeWidth={2} />
                    </Link>
                  </div>
                </div>
              )}

              {/* ── LOGISTICS ── */}
              {activeTab === 'logistics' && <LogisticsTab tour={tour} />}

            </div>
          </div>

          {/* SIDEBAR */}
          <div className="lg:col-span-4">
            <div className="sticky top-8 space-y-4">

              <div style={{ position: 'relative', overflow: 'hidden', background: NAVY, border: '1px solid rgba(184,151,90,.22)' }}>
                <div style={{ height: '2px', background: `linear-gradient(90deg,${GOLD},rgba(184,151,90,.08))` }} />

                <div style={{ padding: '28px 28px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '20px' }}>
                    <div className="avail-dot" style={{ background: departures.length > 0 ? '#4ade80' : '#94a3b8' }} />
                    <span className="f-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.45)' }}>
                      {departures.length > 0 ? `${departures.length} Departures Available` : 'Check Availability'}
                    </span>
                  </div>

                  <p className="f-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,151,90,.7)', marginBottom: '6px' }}>
                    Price per Guest
                  </p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', marginBottom: '4px' }}>
                    <span className="f-cormorant" style={{ fontSize: '58px', fontWeight: 300, color: '#fff', lineHeight: 1 }}>${price.toLocaleString()}</span>
                    <span className="f-jost" style={{ fontSize: '15px', fontStyle: 'normal', color: 'rgba(255,255,255,.28)' }}>USD / person</span>
                  </div>
                  <p className="f-jost" style={{ fontSize: '13px', color: 'rgba(255,255,255,.22)', letterSpacing: '.08em', marginBottom: '22px' }}>
                    Full board · All park fees · Private charter included
                  </p>

                  <div style={{ height: '1px', background: 'rgba(255,255,255,.06)', marginBottom: '20px' }} />

                  {departures.length > 0 && (
                    <>
                      <p className="f-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(184,151,90,.7)', marginBottom: '10px' }}>
                        Select Departure
                      </p>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
                        {departures.slice(0, 4).map((dep, i) => (
                          <button key={dep.id} onClick={() => setSelectedDate(i)} className={`date-chip ${selectedDate === i ? 'sel' : ''}`}>
                            {new Date(`${dep.departureDate}T00:00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                            {dep.spotsRemaining <= 2 && dep.spotsRemaining > 0 && (
                              <span style={{ marginLeft: '4px', color: '#f87171', fontSize: '12px' }}>· {dep.spotsRemaining} left</span>
                            )}
                          </button>
                        ))}
                      </div>

                      {selDep && (
                        <div style={{ marginBottom: '20px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                            <span className="f-jost" style={{ fontSize: '12px', letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(255,255,255,.25)' }}>Capacity</span>
                            <span className="f-jost" style={{ fontSize: '12px', color: 'rgba(255,255,255,.32)' }}>
                              {selDep.spotsRemaining} / {selDep.availableSpots} seats available
                            </span>
                          </div>
                          <div className="cap-bar-track">
                            <div className="cap-bar-fill" style={{ width: `${100 - (selDep.capacityPct || 0)}%` }} />
                          </div>
                        </div>
                      )}
                      <div style={{ height: '1px', background: 'rgba(255,255,255,.06)', marginBottom: '20px' }} />
                    </>
                  )}

                  {primaryGuide && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '22px' }}>
                      {primaryGuide.photo && (
                        <div style={{ width: '40px', height: '40px', flexShrink: 0, overflow: 'hidden', border: '1px solid rgba(184,151,90,.35)' }}>
                          <img src={primaryGuide.photo} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      <div>
                        <p className="f-jost" style={{ fontSize: '12px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: 'rgba(184,151,90,.65)', marginBottom: '2px' }}>Your Lead Guide</p>
                        <p className="f-cormorant" style={{ fontSize: '18px', fontStyle: 'normal', fontWeight: 300, color: '#fff', lineHeight: 1.2 }}>
                          {primaryGuide.user?.fullName || `${primaryGuide.user?.firstName} ${primaryGuide.user?.lastName}`}
                        </p>
                        <p className="f-jost" style={{ fontSize: '13px', color: 'rgba(255,255,255,.28)', fontWeight: 300 }}>{primaryGuide.experienceYears} years in the field</p>
                      </div>
                    </div>
                  )}
                </div>

                <div style={{ padding: '0 28px 24px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <Link to={`/booking/${tour.slug}`} className="reserve-btn">
                    <span>Reserve Your Place</span>
                    <ArrowRight size={13} />
                  </Link>
                  {/* Was "Request Custom Dates" with no action. Starts the builder
                      from this safari — its parks, nights and lodges filled
                      in — so the guest changes only what they want, dates
                      included. */}
                  <Link to="/tour-builder"
                    state={{ fromTour: { slug: tour.slug, name: tour.name, nights: tour.durationNights } }}
                    style={{ width: '100%', padding: '13px', border: '1px solid rgba(255,255,255,.1)', background: 'transparent', color: 'rgba(255,255,255,.62)', cursor: 'pointer', transition: 'all .3s', fontFamily: "'Jost',sans-serif", fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', textAlign: 'center', textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(184,151,90,.4)'; e.currentTarget.style.color = 'rgba(255,255,255,.85)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,.1)'; e.currentTarget.style.color = 'rgba(255,255,255,.62)'; }}>
                    Customise this safari
                  </Link>
                </div>

                <div style={{ borderTop: '1px solid rgba(255,255,255,.06)', padding: '16px 28px' }}>
                  {[
                    { icon: Zap,         label: 'Instant Confirmation', sub: 'Receipt in under 2 minutes' },
                    { icon: Calendar,    label: 'Free Date Transfers',  sub: tour.cancellationPolicy?.freeDateChangesNote || 'Move once, no penalty' },
                    { icon: ShieldCheck, label: tour.cancellationPolicy?.name || '30-Day Cancellation', sub: 'Full refund, no questions' },
                  ].map(({ icon: Icon, label, sub }) => (
                    <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,.04)' }}>
                      <div style={{ width: '28px', height: '28px', flexShrink: 0, border: '1px solid rgba(184,151,90,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon size={12} strokeWidth={1.5} style={{ color: GOLD }} />
                      </div>
                      <div>
                        <p className="f-jost" style={{ fontSize: '13px', fontWeight: 500, letterSpacing: '.14em', color: 'rgba(255,255,255,.55)', marginBottom: '1px' }}>{label}</p>
                        <p className="f-jost" style={{ fontSize: '13px', color: 'rgba(255,255,255,.22)', fontWeight: 300 }}>{sub}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', padding: '16px 18px', border: '1px solid #e8e4de', background: '#fff' }}>
                <div style={{ width: '32px', height: '32px', flexShrink: 0, border: `1px solid ${GOLD_DIM}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <ShieldCheck size={14} strokeWidth={1.5} style={{ color: GOLD }} />
                </div>
                <div>
                  <p className="f-jost" style={{ fontSize: '13px', fontWeight: 600, letterSpacing: '0.16em', textTransform: 'uppercase', color: '#12100e', marginBottom: '2px' }}>Secure Booking</p>
                  <p className="f-jost" style={{ fontSize: '13px', color: '#12100e', fontWeight: 300 }}>256-bit encrypted · PCI DSS compliant</p>
                </div>
              </div>

              <div style={{ border: '1px solid #e8e4de', background: '#FAFAF8', display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                {[
                  [`${tour.averageRating} ★`,   `${tour.totalReviews} reviews`],
                  [tour.durationLabel || `${tour.durationDays} Days`, 'Full board'],
                  [`Max ${tour.maxParticipants}`, 'Private vehicle'],
                  [`${languages.length || 3} langs`, 'Guide languages'],
                ].map(([val, label], i) => (
                  <div key={label} style={{ padding: '16px 18px', borderRight: i % 2 === 0 ? '1px solid #f0ece6' : 'none', borderBottom: i < 2 ? '1px solid #f0ece6' : 'none' }}>
                    <p className="f-cormorant" style={{ fontSize: '25px', fontWeight: 300, color: '#0f172a', lineHeight: 1, marginBottom: '4px' }}>{val}</p>
                    <p className="f-jost" style={{ fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.14em', color: '#12100e' }}>{label}</p>
                  </div>
                ))}
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}